import React from 'react';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';

interface DeleteEventButtonProps {
  eventId: string;
  orgId: string;
}

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

export const DeleteEventButton: React.FC<DeleteEventButtonProps> = ({ eventId, orgId }) => {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const deleteEventMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/org/${orgId}/event/${eventId}`, { method: 'DELETE' });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete event');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizationEvents'] });
      navigate(`/app/org/${orgId}/events`);
    },
  });

  const handleConfirm = () => {
    deleteEventMutation.mutate();
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="destructive">
          <Trash2 className="h-4 w-4" />
          <span className="hidden md:inline">Delete</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Event</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this event? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={deleteEventMutation.status === 'pending'}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={deleteEventMutation.status === 'pending'}>
            {deleteEventMutation.status === 'pending' ? (
              <span className="flex items-center"><span className="animate-spin mr-2 h-4 w-4 border-2 border-t-transparent border-white rounded-full"></span>Deleting...</span>
            ) : 'Confirm Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
